import { css, html, LitElement } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { getGlobalStyleSheets } from '../../global-styles.ts';
import { ButtonEnums } from '../button/index.ts';
import { AtlChatInputProps } from './messaging.props.ts';

import { classMap } from 'lit/directives/class-map.js';
import { ChatInputUpdatedEvent, ChatSentEvent } from './messaging.events.ts';
import * as TagNames from './messaging.tagnames.ts';
import { ifDefined } from 'lit/directives/if-defined.js';

@customElement(TagNames.ChatInput)
export class AtlChatInput extends LitElement implements AtlChatInputProps {
   declare chatInputBoxPlaceholder?: string;
   declare disableChatSend?: boolean;
   declare sendButtonTooltip?: string;

   declare _hasContent?: boolean;

   static properties = {
      chatInputBoxPlaceholder: { type: String },
      disableChatSend: { attribute: 'disable-chat-send', type: Boolean },
      _hasContent: { type: Boolean, state: true },
      sendButtonTooltip: { attribute: 'send-button-tooltip', type: String },
   };

   constructor() {
      super();
      this._hasContent = false;
      this.addEventListener(ChatInputUpdatedEvent.EVENT_NAME, (e: Event) => {
         const customEvent = e as ChatInputUpdatedEvent;
         if (customEvent.detail) {
            this.chatInputBoxValue = customEvent.detail;
         }
      });
   }

   static styles = [
      ...getGlobalStyleSheets(),
      css`
         .messaging-chat-panel-footer {
            padding: 1rem;
            align-items: center;
         }
         .messaging-chat-panel-footer-body {
            width: 100%;
            box-sizing: border-box;
            border: 1px solid #ececec;
            border-radius: 1rem;
            transition: border-color 0.3s ease-out;
            box-shadow: var(--bs-box-shadow-sm);
         }

         .messaging-chat-panel-footer-body:focus-within {
            border-color: var(--bs-gray-600);
         }
         .chat-input-box {
            flex: 1;
            padding: 0.75rem;
            max-height: 100px;
            outline: none;
            overflow-y: auto;
            transition: 0.3s ease-out;
            box-sizing: border-box;
         }

         .chat-input-box:empty::before,
         .chat-input-box:has(br:only-child)::before {
            content: attr(data-placeholder);
            color: #999999;
            pointer-events: none;
            position: absolute;
         }

         .footer-buttons {
            width: 100%;
            gap: 0.5rem;
            padding: 0.25rem;
         }

         .messaging-chat-panel-footer-extended {
            display: flex;
            justify-content: center;
            font-size: 12px;
            color: #6e6e6e;
         }

         @media (max-width: 768px) {
            .messaging-chat-panel-footer {
               display: block !important;
            }
            .chat-input-box {
               width: 100%;
            }
            .footer-buttons {
               justify-content: end;
            }
            .messaging-chat-panel-footer-extended {
               display: block !important;
               justify-content: center;
            }
         }

         .send-button-wrapper {
            opacity: 0;
            transform: translateX(10px) rotate(-120deg);
            transition:
               opacity 225ms ease-out,
               transform 225ms ease-out;
            pointer-events: none;
         }

         .send-button-wrapper:not(.visible) {
            visibility: hidden;
         }

         .send-button-wrapper.visible {
            opacity: 1;
            transform: translateX(0) rotate(0deg);
            pointer-events: auto;
            visibility: visible;
         }
      `,
   ];

   @query('.chat-input-box')
   private inputElement!: HTMLDivElement;

   private get chatInputBoxValue(): string | undefined {
      return this.inputElement?.textContent ?? undefined;
   }

   private set chatInputBoxValue(value: string) {
      this.inputElement.textContent = value;
      const trimmedValue = value.trim();
      this._hasContent = trimmedValue.length > 0;
   }

   private _handleInput = (event: InputEvent) => {
      const textContent = (event.target as HTMLDivElement).textContent ?? '';
      this._hasContent = textContent.trim().length > 0;
   };

   handleKeyPress(event: KeyboardEvent) {
      if (this.disableChatSend) return;

      if (event.code.toLowerCase() === 'enter') {
         event.preventDefault();
         this._onSentClick();
      }
   }

   private _onSentClick = () => {
      if (this.disableChatSend) return;
      if (!this.chatInputBoxValue?.trim()?.length) return;

      this.dispatchEvent(new ChatSentEvent(this.chatInputBoxValue.trim()));
      this.chatInputBoxValue = '';
   };

   render() {
      return html`
         <div class="messaging-chat-panel-footer">
            <div class="messaging-chat-panel-footer-body">
               <slot name="context"></slot>
               <div class="d-flex">
                  <div
                     part="chat-text-input"
                     class=${classMap({
                        'chat-input-box': true,
                     })}
                     contenteditable="plaintext-only"
                     data-placeholder=${this.chatInputBoxPlaceholder ??
                     'Type your message'}
                     aria-multiline="true"
                     aria-label="Chat message input box"
                     tabindex="0"
                     role="textbox"
                     @input=${this._handleInput}
                     @keypress=${this.handleKeyPress}
                  ></div>
               </div>
               <div class="d-flex align-content-center footer-buttons">
                  <slot name="actions"></slot>
                  <div class="col d-flex justify-content-end">
                     <div
                        class=${classMap({
                           'send-button-wrapper': true,
                           visible: this._hasContent === true,
                        })}
                        ?inert=${!this._hasContent}
                        aria-hidden=${!this._hasContent ? 'true' : 'false'}
                     >
                        <atlantis-button
                           variant=${ButtonEnums.ButtonVariant.Action}
                           button-classes="text-secondary"
                           iconName="paper-plane"
                           ?disabled=${this.disableChatSend}
                           .buttonTooltip=${ifDefined(this.sendButtonTooltip)}
                           data-bs-toggle=${ifDefined(
                              this.sendButtonTooltip ? 'tooltip' : undefined
                           )}
                           @click=${this._onSentClick}
                           .buttonAriaSettings=${{
                              ariaLabel: 'Send message',
                           }}
                        >
                        </atlantis-button>
                     </div>
                  </div>
               </div>
            </div>
            <div class="mt-2 messaging-chat-panel-footer-extended">
               <slot></slot>
            </div>
         </div>
      `;
   }
}
